// @flow

import { Link } from "gatsby";
import React, { useEffect, useState } from "react";

import ContinueLink from "@/components/ContinueLink/ContinueLink";
import MenuContext from "@/context/MenuContext";
import TwinbruLogo from "@/images/BruFabrics-logo.inline.svg";
import { classNameOf } from "@/utils";

type NavigationItem = {
    name: string,
    path: string,
};

/**
 * The list of navigation items to be rendered in the header.
 *
 * @type {NavigationItem[]}
 */
const navigationItems: NavigationItem[] = [
    {
        name: "Products & Brands",
        path: "/products-and-brands",
    },
    {
        name: "Sustainability",
        path: "/sustainability",
    },
    {
        name: "Solutions",
        path: "/solutions",
    },
    {
        name: "Customers",
        path: "/customers",
    },
    {
        name: "Jobs",
        path: "https://jobs.brutex.com/",
    },
    {
        name: "Group",
        path: "/group",
    },
    {
        name: "Contact",
        path: "/contact",
    },
];

type HeaderState = {
    windowY: number,
    windowYPrev: number,
    windowYD: number,
    navbarIn: boolean,
    navbarStuck: boolean,
    isMobileMenu: boolean,
};

type Props = {
    /**
     * Header style type, defaults to "light"
     */
    type?: "light" | "dark" | "transparent",
};

const Header = (props: Props): React$Element<'header'> => {
    const navbarRef = React.createRef<HTMLElement>();

    const [state, setState] = useState<HeaderState>({
        windowY: 0,
        windowYPrev: 0,
        windowYD: 0,
        navbarIn: false,
        navbarStuck: true,
        isMobileMenu: false,
    });

    function bruNavbar() {
        const windowYPrev = state.windowYPrev;
        const windowY = window.scrollY || window.pageYOffset;
        const windowYD = windowYPrev - windowY;

        function isNavbarIn() {
            if (windowY < 10) return false;
            if (windowYD < -1 && !state.navbarIn) return true;
            if (windowYD > 1 && state.navbarIn) return false;
            return state.navbarIn;
        }

        function isStuck() {
            if (windowY < 10) return true;
            if (windowYD < -1 && !state.navbarIn) return false;
            if (windowYD > 1 && state.navbarIn) return false;
            return state.navbarStuck;
        }

        const stateUpdate = {
            navbarStuck: isStuck(),
            navbarIn: isNavbarIn(),
            windowYPrev: windowY,
        };

        setState(prevState => ({
            ...prevState,
            ...stateUpdate,
        }));
    }

    // This useEffect is called on mount and whenever state/props update.
    useEffect(() => {
        document.addEventListener("scroll", bruNavbar);

        // Clean up this effect.
        return () => document.removeEventListener("scroll", bruNavbar);
    });

    // This useEffect is only called on mount, see the second argument `[]`
    useEffect(() => {
        bruNavbar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (document.body) {
            document.body.className = classNameOf({
                "show-mobile-menu": state.isMobileMenu,
            });
        }
    }, [state.isMobileMenu])

    function toggleMobileMenu() {
        setState(prevState => ({
            ...prevState,
            isMobileMenu: !prevState.isMobileMenu,
        }));
    }

    const navItems = navigationItems.map(item => (
        <li key={item.path}>
            {/* Use Link from "gatsby" so linked pages can be preloaded */}
            <Link
                to={item.path}
                partiallyActive={true}
                className="nav-link"
                activeClassName="link-active"
            >
                {item.name}
            </Link>
        </li>
    ));

    const classNames = classNameOf("bru-navbar", props.type || "", {
        in: state.navbarIn,
        stuck: state.navbarStuck,
    });

    return (
        <header className={classNames} ref={navbarRef}>
            <div className="header-col-start">
                <Link to="/">
                    <div className="svg-box icon-logo logo-sm">
                        <div className="svg-container">
                            <TwinbruLogo />
                        </div>
                    </div>
                </Link>
            </div>

            <nav className="header-col-middle">
                <div className="bru-navbar-collapse">
                    <ul>{navItems}</ul>

                    <div className="btn-wrap d-xl-none">
                        <ContinueLink
                            to="https://portal.twinbru.com/en/account"
                            variant="boxed-light-gray"
                            target="_blank"
                            styles="continue-link-sm"
                        >
                            Login
                        </ContinueLink>
                    </div>
                </div>
            </nav>

            <div className="header-col-end">
                <ContinueLink
                    to="https://portal.twinbru.com/en/account"
                    variant="boxed-light-gray"
                    target="_blank"
                    styles="continue-link-sm"
                >
                    Client login
                </ContinueLink>
            </div>

            <MenuContext.Provider value={{ isMobile: state.isMobileMenu }}>
                <div
                    className="nav-toggler float-left ml-4 d-xl-none"
                    onClick={() => toggleMobileMenu()}
                >
                    <div className="line line-1 transition position-absolute" />

                    <div className="line line-2 transition position-absolute" />
                </div>
            </MenuContext.Provider>
        </header>
    );
};

export default Header;
